import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import api, { specialUserApi } from 'lib/api';
import styles from './MembershipPhotoUpload.module.scss';

function resolveKennitala(member) {
    const raw = member?.torgid_user?.kennitala || member?.kennitala || '';
    const digits = String(raw).replace(/\D+/g, '');
    return digits.length === 10 ? digits : null;
}

export default function MembershipPhotoEditDialog({
    open,
    onClose,
    member,
    groups,
    onSaved,
}) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [photos, setPhotos] = useState({});

    const kennitala = resolveKennitala(member);

    const memberGroups = useMemo(() => {
        if (!member || !groups?.length) return [];
        const ids = String(member.torgid_special_companies || '')
            .split(',')
            .map((id) => Number(id))
            .filter(Boolean);
        return groups.filter((g) => ids.includes(g.id));
    }, [member, groups]);

    React.useEffect(() => {
        if (!open || !member) return;
        const initial = {};
        memberGroups.forEach((g) => {
            initial[g.id] = member.membership_photos?.[g.id] || member.membership_photos?.[String(g.id)] || '';
        });
        setPhotos(initial);
    }, [open, member, memberGroups]);

    const handleSave = async () => {
        if (!kennitala) return;
        setSaving(true);
        try {
            for (const group of memberGroups) {
                const value = photos[group.id];
                const original =
                    member.membership_photos?.[group.id] ||
                    member.membership_photos?.[String(group.id)] ||
                    '';

                if (value && String(value).startsWith('data:')) {
                    await api.put(specialUserApi.upsertMembershipPhoto(), {
                        group_id: group.id,
                        kennitala,
                        image: value,
                    });
                } else if (!value && original) {
                    await api.delete(specialUserApi.deleteMembershipPhoto(), {
                        params: { group_id: group.id, kennitala },
                    });
                }
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('membership_photo_edit_title')}</DialogTitle>
            <DialogContent dividers>
                {!kennitala ? (
                    <Typography color="error">{t('membership_photo_no_kennitala')}</Typography>
                ) : (
                    memberGroups.map((group) => (
                        <div key={group.id} className={styles.groupRow}>
                            <div className={styles.passportFrame}>
                                <SingleImageUpload
                                    image={photos[group.id] || ''}
                                    aspectRatio="7/9"
                                    cropShape="rect"
                                    onUpload={(data) =>
                                        setPhotos((prev) => ({ ...prev, [group.id]: data }))
                                    }
                                    onDelete={() =>
                                        setPhotos((prev) => ({ ...prev, [group.id]: '' }))
                                    }
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                            <div className={styles.groupMeta}>
                                <p className={styles.groupName}>{group.name}</p>
                                <p className={styles.hint}>{t('membership_photo')}</p>
                            </div>
                        </div>
                    ))
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={saving || !kennitala}
                >
                    {saving ? <CircularProgress size={20} /> : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function MembershipPhotoThumb({ member, groupId, onClick, disabled }) {
    const url =
        member?.membership_photos?.[groupId] ||
        member?.membership_photos?.[String(groupId)] ||
        Object.values(member?.membership_photos || {})[0] ||
        '';

    return (
        <button
            type="button"
            className={styles.thumbButton}
            onClick={onClick}
            disabled={disabled}
            title={disabled ? undefined : undefined}
        >
            {url ? (
                <img src={url} alt="" className={styles.thumb} />
            ) : (
                <span className={styles.thumbPlaceholder}>+</span>
            )}
        </button>
    );
}

export { resolveKennitala };
